import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import {
    Modal,
    Backdrop,
    Fade,
    Box,
    Typography,
    Button,
    makeStyles,
    CircularProgress,
    TextField,
    Grid,
} from '@material-ui/core'

import {
    useAreaLabels,
    AreaLabelCombinations,
    UpdateAreaLabelRequest,
} from '../../hooks/useAreaLabels'

interface EditAreaLabelsModalProps {
    open: boolean
    onClose: () => void
    companyId?: number
    title?: string
    children?: React.ReactNode
}

interface LabelChange {
    area_config_id: number
    area_config_name: string
    original_label: string
    new_label: string
}

const useStyles = makeStyles((theme) => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    paper: {
        backgroundColor: theme.palette.background.paper,
        border: '2px solid #000',
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 3),
        minWidth: '700px',
        maxWidth: '1000px',
        maxHeight: 'calc(100vh - 300px)',
        minHeight: 'calc(100vh - 300px)',
        display: 'flex',
        flexDirection: 'column',
    },
    title: {
        marginBottom: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        borderBottom: `1px solid ${theme.palette.divider}`,
        marginLeft: -theme.spacing(4),
        marginRight: -theme.spacing(4),
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
    },
    content: {
        flex: 1,
        overflow: 'auto',
        marginBottom: theme.spacing(3),
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
    },
    row: {
        marginBottom: theme.spacing(2),
    },
    labelColumn: {
        display: 'flex',
        alignItems: 'center',
    },
    columnHeader: {
        fontWeight: 'bold',
    },
}))

const EditAreaLabelsModal: React.FC<EditAreaLabelsModalProps> = ({
    open,
    onClose,
    companyId,
    title = 'Edit Area Labels',
    children,
}) => {
    const classes = useStyles()
    const {
        areaLabelCombinations,
        getAreaLabelCombinations,
        updateAreaLabels,
    } = useAreaLabels()
    const [editedLabels, setEditedLabels] = useState<
        Record<string, LabelChange>
    >({})
    const [isSubmitting, setIsSubmitting] = useState(false)

    useEffect(() => {
        if (open) {
            getAreaLabelCombinations(companyId)
            setEditedLabels({})
        }
    }, [open])

    const getUniqueKey = (combination: AreaLabelCombinations) => {
        return `${combination.area_config_id}-${combination.area_config_name}-${combination.area_label}`
    }

    const handleLabelChange = (
        combination: AreaLabelCombinations,
        newLabel: string,
    ) => {
        const uniqueKey = getUniqueKey(combination)
        setEditedLabels((prev) => ({
            ...prev,
            [uniqueKey]: {
                area_config_id: combination.area_config_id,
                area_config_name: combination.area_config_name,
                original_label: combination.area_label,
                new_label: newLabel,
            },
        }))
    }

    const handleSave = async () => {
        try {
            setIsSubmitting(true)
            // Prepare changes for backend submission
            const changes: UpdateAreaLabelRequest = {
                area_label_combinations: Object.values(editedLabels)
                    .filter((change) => change.new_label.trim() !== '')
                    .map((change) => ({
                        area_config_id: change.area_config_id,
                        area_config_name: change.area_config_name,
                        current_area_label: change.original_label,
                        new_area_label: change.new_label,
                    })),
            }

            await updateAreaLabels(changes, companyId).then(() => {
                toast.success('Area labels updated successfully')
                onClose()
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Modal
            className={classes.modal}
            open={open}
            onClose={onClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 500,
            }}
        >
            <Fade in={open}>
                <div className={classes.paper}>
                    <Typography
                        variant="h6"
                        component="h2"
                        className={classes.title}
                    >
                        {title}
                    </Typography>
                    <Box className={classes.content}>
                        {!areaLabelCombinations.length ? (
                            <Box className={classes.loadingContainer}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                <Grid container>
                                    <Grid item xs={6}>
                                        <Typography
                                            variant="subtitle1"
                                            className={classes.columnHeader}
                                        >
                                            Area Configuration
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography
                                            variant="subtitle1"
                                            className={classes.columnHeader}
                                        >
                                            New Label
                                        </Typography>
                                    </Grid>
                                </Grid>
                                {areaLabelCombinations.map((combination) => {
                                    const uniqueKey = getUniqueKey(combination)
                                    return (
                                        <Grid
                                            container
                                            key={uniqueKey}
                                            className={classes.row}
                                        >
                                            <Grid
                                                item
                                                xs={6}
                                                className={classes.labelColumn}
                                            >
                                                <Typography>
                                                    {
                                                        combination.area_config_name
                                                    }{' '}
                                                    ({combination.area_label})
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    variant="outlined"
                                                    size="small"
                                                    placeholder={
                                                        combination.area_label
                                                    }
                                                    value={
                                                        editedLabels[uniqueKey]
                                                            ?.new_label || ''
                                                    }
                                                    onChange={(e) =>
                                                        handleLabelChange(
                                                            combination,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    )
                                })}
                            </>
                        )}
                    </Box>
                    <Box className={classes.actions}>
                        <Button
                            onClick={onClose}
                            color="primary"
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                            disabled={
                                Object.keys(editedLabels).length === 0 ||
                                isSubmitting
                            }
                        >
                            {isSubmitting ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </Box>
                </div>
            </Fade>
        </Modal>
    )
}

export default EditAreaLabelsModal
